import classNames from 'classnames';
import { StaticImage } from 'gatsby-plugin-image';
import { useEffect, useState } from 'react';

import { Phone } from '~components/AllianceCallService/AllianceCallService';
import Button from '~components/Button/Button';
import { usePageContext } from '~components/PageDataProvider/PageDataProvider';
import { setVisitedQuotePageCookie } from '~globals/common';
import constants from '~globals/constants';
import { fireTrackEvent } from '~globals/tracking';
import { useAppSelector } from '~hooks/useRedux';
import useWindowSize from '~hooks/useWindowSize';

const logoSrc = '../../../images/logos/eh-logo-twocolor.png';

type Props = {
    backgroundsurface?: SupportedBackgroundColor;
    textcolor?: SupportedTextColor;
    maincopy?: string;
    mainctacopy?: string;
    plantype: PlansType;
};

// TODO: combine this component with ScrollToSticky
// TODO: currently this component renders inside <PostContent> -- it should be one level up in proper
// TODO: semantic fashion (but since it's absolutely positioned and not SEO-critical it works for now.)

// Default text values for maincopy:
// MA: Compare the nation's top Medicare plans. All in one place. (every page except eperks)
// MS: Call to find Medicare Supplement plans (enroll/medicare-supplement/)

const StickyCtaBanner = ({
    backgroundsurface = 'bg-surface-300',
    textcolor = 'text-primary-dark',
    maincopy = "Compare the nation's top Medicare plans. All in one place.",
    mainctacopy = 'See plans in my area',
    plantype = 'MA',
}: Props) => {
    const [didScrollPastHero, setDidScrollPastHero] = useState(false);

    const { stateAbbr, countyName } = useAppSelector((state) => state.global.geoLocation);

    const { isDesktop } = useWindowSize();

    const handleScroll = () => {
        if (typeof window === 'undefined') return;
        const { scrollY } = window;
        const hero = document.querySelector('[data-qa="landing-page-hero"]') as HTMLDivElement;
        if (!hero) return;
        setDidScrollPastHero(scrollY > hero.offsetHeight + hero.offsetTop);
    };

    useEffect(() => {
        if (typeof window === 'undefined') return;
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    const trackingComponentName = isDesktop ? 'Desktop Sticky' : 'Sticky';
    const ctaButtonClicked = () => {
        setVisitedQuotePageCookie();
        fireTrackEvent({ page: { action: `${trackingComponentName}: See Plans Sticky` } });
    };

    const isExcludedCounty =
        stateAbbr && constants.EXCLUDED_COUNTIES.includes(`${countyName}, ${stateAbbr}`);
    const { pathname } = usePageContext().location;
    const pageFilter = !isExcludedCounty
        ? pathname.includes('/enroll/dvh/')
            ? '2%3Adrugs%2Cdental'
            : pathname.includes('/enroll/zeropremium/')
              ? '2:drugs%7C1:0.0&category=ZeroMonthlyPremium'
              : undefined
        : undefined;

    return (
        <article
            className={classNames(
                'fixed',
                'bottom-0 lg:top-0 lg:bottom-auto',
                'w-screen',
                'p-4 lg:py-2',
                'justify-center items-center',
                plantype !== 'MS' ? 'gap-6' : 'gap-12',
                backgroundsurface,
                didScrollPastHero ? 'block md:hidden lg:flex' : 'hidden'
            )}
        >
            <span className="hidden lg:block">
                <StaticImage src={logoSrc} alt="eHealth logo" height={32} />
            </span>
            <p className="hidden max-w-[520px] lg:block ipadpro:max-w-fit text-24 leading-32 font-bold">
                <span>{maincopy}</span>
            </p>
            {plantype !== 'MS' ? (
                <Button
                    islink={true}
                    forceATag={true}
                    styletype="primary"
                    className="w-full lg:w-auto"
                    onClick={() => ctaButtonClicked()}
                    href={process.env.GATSBY_EHMP_URL}
                    data-filter={pageFilter}
                >
                    {mainctacopy}
                </Button>
            ) : (
                <div className="flex items-center justify-center">
                    {isDesktop ? (
                        <>
                            <Phone classname="text-link-visited text-24 leading-32 font-bold" />
                            <span className="hidden lg:block ml-2 text-24 leading-32">TTY 711</span>
                        </>
                    ) : (
                        <Phone isbtn="primary" />
                    )}
                </div>
            )}
        </article>
    );
};

export default StickyCtaBanner;
